import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Image, Search, X, Loader2, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useQRStore } from '@/stores/qrStore';
import { prefilledLogos, qrTypeToLogo } from '@/lib/design-constants';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export function LogoTab() {
  const { currentQR, setLogoSettings } = useQRStore();
  const { designSettings } = currentQR;
  const [searchQuery, setSearchQuery] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);

  const suggestedLogoId = qrTypeToLogo[currentQR.type];

  const filteredLogos = prefilledLogos.filter(logo =>
    logo.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    logo.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedLogos = {
    general: filteredLogos.filter(l => l.category === 'general'),
    social: filteredLogos.filter(l => l.category === 'social'),
    payment: filteredLogos.filter(l => l.category === 'payment'),
    business: filteredLogos.filter(l => l.category === 'business'),
    symbols: filteredLogos.filter(l => l.category === 'symbols'),
  };

  const handleFileUpload = useCallback(async (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max size is 5MB');
      return;
    }

    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Use PNG, JPG, SVG, or WebP');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Convert to base64 data URL locally to avoid cross-origin issues
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 90));
          }
        };
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

      setUploadProgress(100);
      setLogoSettings({ enabled: true, url: dataUrl });
      toast.success('Logo added successfully!');
    } catch (error: any) {
      toast.error('Failed to load logo: ' + error.message);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [setLogoSettings]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  }, [handleFileUpload]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const selectPrefilledLogo = (logoId: string) => {
    const logo = prefilledLogos.find(l => l.id === logoId);
    if (!logo) return;

    const color = designSettings.dotsColor || '#000000';
    const size = 128;

    // Create a hidden container, render the Lucide React icon into it, extract SVG
    const container = document.createElement('div');
    container.style.cssText = 'position:absolute;left:-9999px;top:-9999px';
    document.body.appendChild(container);

    // Use flushSync to render synchronously so we can immediately read the SVG
    import('react-dom/client').then(async ({ createRoot }) => {
      const { createElement } = await import('react');
      const { flushSync } = await import('react-dom');
      
      const root = createRoot(container);
      flushSync(() => {
        root.render(createElement(logo.icon, { size, color, strokeWidth: 2 }));
      });

      const svgEl = container.querySelector('svg');
      if (svgEl) {
        svgEl.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        // Get the original viewBox (Lucide uses 0 0 24 24)
        const vb = svgEl.getAttribute('viewBox') || '0 0 24 24';
        const [, , vbW, vbH] = vb.split(' ').map(Number);
        // Set explicit size and matching viewBox
        svgEl.setAttribute('width', String(size));
        svgEl.setAttribute('height', String(size));
        svgEl.setAttribute('viewBox', `0 0 ${vbW} ${vbH}`);
        // Add a background circle using viewBox coordinates (not pixel size)
        const bg = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        bg.setAttribute('cx', String(vbW / 2));
        bg.setAttribute('cy', String(vbH / 2));
        bg.setAttribute('r', String(vbW / 2));
        bg.setAttribute('fill', designSettings.backgroundColor || '#ffffff');
        svgEl.insertBefore(bg, svgEl.firstChild);
        
        const svgString = new XMLSerializer().serializeToString(svgEl);
        const svgDataUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgString)))}`;
        
        // Rasterize SVG to PNG to avoid rendering artifacts in qr-code-styling
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, size, size);
            const pngDataUrl = canvas.toDataURL('image/png');
            setLogoSettings({ enabled: true, url: pngDataUrl });
          } else {
            setLogoSettings({ enabled: true, url: svgDataUrl });
          }
        };
        img.onerror = () => {
          setLogoSettings({ enabled: true, url: svgDataUrl });
        };
        img.src = svgDataUrl;

        root.unmount();
        document.body.removeChild(container);
      } else {
        toast.error('Failed to render icon');
        root.unmount();
        document.body.removeChild(container);
      }
    }).catch(() => {
      toast.error('Failed to load icon');
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    });
  };

  const removeLogo = () => {
    setLogoSettings({ enabled: false, url: '' });
  };

  return (
    <div className="space-y-6">
      {/* Logo Enable Toggle */}
      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
        <div className="space-y-0.5">
          <Label>Enable Logo</Label>
          <p className="text-xs text-muted-foreground">Add a logo to your QR code center</p>
        </div>
        <Switch
          checked={designSettings.logo.enabled}
          onCheckedChange={(checked) => setLogoSettings({ enabled: checked })}
        />
      </div>

      <AnimatePresence>
        {designSettings.logo.enabled && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-6"
          >
            {/* Current Logo Preview */}
            {designSettings.logo.url && (
              <div className="relative p-4 rounded-lg border border-border/50 bg-muted/30">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-lg bg-background flex items-center justify-center border border-border">
                    <img 
                      src={designSettings.logo.url} 
                      alt="Current logo"
                      className="w-12 h-12 object-contain"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Current Logo</p>
                    <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                      {designSettings.logo.url.split('/').pop()}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={removeLogo}
                    className="text-destructive hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Upload Area */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={cn(
                'relative border-2 border-dashed rounded-xl p-6 transition-all duration-200 text-center',
                dragActive
                  ? 'border-primary bg-primary/5'
                  : 'border-border/50 hover:border-primary/50'
              )}
            >
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={isUploading}
              />
              
              {isUploading ? (
                <div className="space-y-3">
                  <Loader2 className="h-8 w-8 mx-auto animate-spin text-primary" />
                  <div className="w-full max-w-xs mx-auto bg-muted rounded-full h-2">
                    <motion.div
                      className="h-full bg-primary rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">Uploading... {uploadProgress}%</p>
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm font-medium">
                    Drop your logo here or <span className="text-primary">browse</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    PNG, JPG, SVG or WebP • Max 5MB
                  </p>
                </>
              )}
            </div>

            {/* Suggested Logo */}
            {suggestedLogoId && (
              <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                <div className="flex items-center gap-3">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span className="text-sm">Suggested for {currentQR.type}:</span>
                  {(() => {
                    const suggested = prefilledLogos.find(l => l.id === suggestedLogoId);
                    if (suggested) {
                      const Icon = suggested.icon;
                      return (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => selectPrefilledLogo(suggestedLogoId)}
                          className="gap-2"
                        >
                          <Icon className="h-4 w-4" />
                          {suggested.name}
                        </Button>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            )}

            {/* Icon Library */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Image className="h-4 w-4 text-primary" />
                <Label className="text-sm font-medium">Icon Library</Label>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search icons..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              <div className="max-h-[300px] overflow-y-auto space-y-4">
                {Object.entries(groupedLogos).map(([category, logos]) => (
                  logos.length > 0 && (
                    <div key={category} className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {category}
                      </p>
                      <div className="grid grid-cols-4 gap-2">
                        {logos.map((logo, index) => {
                          const Icon = logo.icon;
                          return (
                            <Tooltip key={logo.id}>
                              <TooltipTrigger asChild>
                                <motion.button
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: index * 0.02 }}
                                  onClick={() => selectPrefilledLogo(logo.id)}
                                  className="flex flex-col items-center gap-1 p-2 rounded-lg border border-border/50 hover:border-primary/50 hover:bg-muted/50 transition-all"
                                >
                                  <Icon className="h-5 w-5" />
                                </motion.button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{logo.name}</p>
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </div>
                  )
                ))}
              </div>
            </div>

            {/* Logo Settings */}
            <div className="space-y-4 p-4 rounded-lg border border-border/50">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>Logo Size</Label>
                  <span className="text-sm text-muted-foreground">{designSettings.logo.size}%</span>
                </div>
                <Slider
                  value={[designSettings.logo.size]}
                  onValueChange={([value]) => setLogoSettings({ size: value })}
                  min={10}
                  max={35}
                  step={1}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Transparent Background</Label>
                <Switch
                  checked={designSettings.logo.transparentBackground}
                  onCheckedChange={(checked) => setLogoSettings({ transparentBackground: checked })}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
